/**
 * Admin Page
 *
 * Purpose: Container page for admin functionality including document library management,
 * prompt templates, and user management
 *
 * Features:
 * - Document library management (pgvector / Sprint 3.5)
 * - Prompt template management (Sprint 4 / Airline Customization)
 * - User management with approval workflow (Sprint 5)
 * - Super Admin access to system configuration
 * - People tab: Users + Onboarding sub-tabs with live badge indicators
 *
 * Access: Admin/Instructor role only
 *
 * Sprint: 5 (User Onboarding)
 * Author: Edward Berger + AI Assistant
 * Date: 2025-11-01
 */

import { useState, useEffect } from 'react';
import { DocumentsAdmin } from '../components/admin/DocumentsAdmin';
import { PromptTemplatesAdmin } from '../components/admin/PromptTemplatesAdmin';
import { TranscriptionScenariosAdmin } from '../components/admin/TranscriptionScenariosAdmin';
import OnboardingAdmin from './admin/OnboardingAdmin';
import { UsersAdmin } from './admin/UsersAdmin';
import PilotsAdmin from './admin/PilotsAdmin';
import { useAuth } from '../contexts/AuthContext';
import { useAirline } from '../contexts/AirlineContext';
import { Navigate, useNavigate } from 'react-router-dom';
import { FileText, MessageSquare, Mic, Shield, UserCircle, UserPlus, Users, ChevronRight, ArrowLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';

type AdminTab = 'documents' | 'prompts' | 'scenarios' | 'pilots' | 'people';
type PeopleSubTab = 'users' | 'onboarding';

export default function Admin() {
  const { user } = useAuth();
  const { isSuperAdmin } = useAirline();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<AdminTab>('documents');
  const [activePeopleTab, setActivePeopleTab] = useState<PeopleSubTab>('users');
  const [mobileView, setMobileView] = useState<'menu' | 'content'>('menu');
  const [newProspectCount, setNewProspectCount] = useState(0);
  const [pendingUserCount, setPendingUserCount] = useState(0);

  // Check if user has admin role
  const isAdmin = user?.user_metadata?.role === 'admin' || user?.user_metadata?.role === 'instructor';
  // Narrower: airline admins only (not instructors). Used to gate User Management
  // and Prompt Templates — both are admin-only configuration surfaces.
  const isAirlineAdmin = user?.user_metadata?.role === 'admin';

  // Combined badge for the People top-level tab
  const totalPeopleCount = pendingUserCount + newProspectCount;

  // If a non-admin somehow has activeTab pointing at an admin-only tab
  // (URL state, hot-reload, role downgrade, etc.), bounce them to documents.
  useEffect(() => {
    if (activeTab === 'prompts' && !(isAirlineAdmin || isSuperAdmin)) {
      setActiveTab('documents');
    }
    if (activeTab === 'people' && !isSuperAdmin) {
      setActiveTab('documents');
    }
  }, [activeTab, isAirlineAdmin, isSuperAdmin]);

  // Fetch pending user count for badge on Users tab (super admins only)
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetch = async () => {
      const { count, error } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .eq('approval_status', 'pending');
      if (!error && count !== null) setPendingUserCount(count);
    };
    fetch();
    // No filter — the filter would only match rows where the NEW value is
    // 'pending', so status transitions away from pending (approve/reject/revoke)
    // would never fire and the badge would never decrement.
    const sub = supabase
      .channel('admin-pending-users-count')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => fetch())
      .subscribe();
    return () => { sub.unsubscribe(); };
  }, [isSuperAdmin]);

  // Fetch new-prospect count for badge on Onboarding tab (super admins only)
  useEffect(() => {
    if (!isSuperAdmin) return;

    const fetchNewProspectCount = async () => {
      const { count, error } = await supabase
        .from('prospects')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'new');
      if (!error && count !== null) setNewProspectCount(count);
    };
    fetchNewProspectCount();

    const sub = supabase
      .channel('new-prospect-count')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'prospects' },
        () => fetchNewProspectCount(),
      )
      .subscribe();
    return () => {
      sub.unsubscribe();
    };
  }, [isSuperAdmin]);

  // Redirect non-admins to dashboard
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const enterSection = (tab: AdminTab) => {
    setActiveTab(tab);
    setMobileView('content');
  };

  const sectionTitle: Record<AdminTab, string> = {
    documents: 'Document Library',
    prompts: 'Prompt Templates',
    scenarios: 'Transcription Scenarios',
    pilots: 'Pilots',
    people: 'People',
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 border-l-4 border-orca-elevate">
        <h1 className="text-2xl sm:text-3xl font-bold text-navy">Admin Panel</h1>
        <p className="text-steel mt-1 sm:mt-2 text-sm sm:text-base">
          Manage system settings, document libraries, prompt templates, and user approvals
        </p>
      </div>

      {/* ── MOBILE: section list ── */}
      {mobileView === 'menu' && (
        <div className="md:hidden bg-white rounded-xl shadow-sm border border-steel/20 overflow-hidden divide-y divide-steel/10">
          <button onClick={() => enterSection('documents')} className="w-full flex items-center gap-4 px-4 py-4 hover:bg-ice transition">
            <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center shrink-0">
              <FileText size={20} className="text-orca-elevate" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-sm text-navy">Document Library</p>
              <p className="text-xs text-steel mt-0.5">SOPs, rubrics and reference manuals</p>
            </div>
            <ChevronRight size={18} className="text-steel/40" />
          </button>

          {(isAirlineAdmin || isSuperAdmin) && (
            <button onClick={() => enterSection('prompts')} className="w-full flex items-center gap-4 px-4 py-4 hover:bg-ice transition">
              <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center shrink-0">
                <MessageSquare size={20} className="text-orca-elevate" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-sm text-navy">Prompt Templates</p>
                <p className="text-xs text-steel mt-0.5">AI prompt customisation per airline</p>
              </div>
              <ChevronRight size={18} className="text-steel/40" />
            </button>
          )}

          <button onClick={() => enterSection('scenarios')} className="w-full flex items-center gap-4 px-4 py-4 hover:bg-ice transition">
            <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center shrink-0">
              <Mic size={20} className="text-orca-elevate" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-sm text-navy">Transcription Scenarios</p>
              <p className="text-xs text-steel mt-0.5">Speaker roles and session context</p>
            </div>
            <ChevronRight size={18} className="text-steel/40" />
          </button>

          <button onClick={() => enterSection('pilots')} className="w-full flex items-center gap-4 px-4 py-4 hover:bg-ice transition">
            <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center shrink-0">
              <UserCircle size={20} className="text-orca-elevate" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-semibold text-sm text-navy">Pilots</p>
              <p className="text-xs text-steel mt-0.5">Pilot profiles and licence records</p>
            </div>
            <ChevronRight size={18} className="text-steel/40" />
          </button>

          {isSuperAdmin && (
            <button onClick={() => enterSection('people')} className="w-full flex items-center gap-4 px-4 py-4 hover:bg-ice transition">
              <div className="w-10 h-10 rounded-full bg-orca-elevate/10 flex items-center justify-center shrink-0">
                <Users size={20} className="text-orca-elevate" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-sm text-navy">People</p>
                <p className="text-xs text-steel mt-0.5">User approvals and onboarding</p>
              </div>
              {totalPeopleCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full mr-1">{totalPeopleCount}</span>
              )}
              <ChevronRight size={18} className="text-steel/40" />
            </button>
          )}

          {isSuperAdmin && (
            <button onClick={() => navigate('/admin/doc-types')} className="w-full flex items-center gap-4 px-4 py-4 hover:bg-ice transition">
              <div className="w-10 h-10 rounded-full bg-navy/10 flex items-center justify-center shrink-0">
                <Shield size={20} className="text-navy" />
              </div>
              <div className="flex-1 text-left">
                <p className="font-semibold text-sm text-navy">Super Admin</p>
                <p className="text-xs text-steel mt-0.5">System configuration and doc types</p>
              </div>
              <ChevronRight size={18} className="text-steel/40" />
            </button>
          )}
        </div>
      )}

      {/* ── MOBILE: section content ── */}
      {mobileView === 'content' && (
        <div className="md:hidden space-y-4">
          {/* Back button + section title */}
          <button
            onClick={() => setMobileView('menu')}
            className="flex items-center gap-2 text-sm font-medium text-orca-elevate"
          >
            <ArrowLeft size={16} />
            {sectionTitle[activeTab]}
          </button>

          {/* People sub-tabs */}
          {activeTab === 'people' && isSuperAdmin && (
            <div className="flex gap-1 bg-white rounded-lg border border-steel/20 p-1">
              <button
                onClick={() => setActivePeopleTab('users')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition ${
                  activePeopleTab === 'users'
                    ? 'bg-orca-elevate text-white shadow-sm'
                    : 'text-steel hover:bg-ice'
                }`}
              >
                <Users size={15} />
                Users
                {pendingUserCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">{pendingUserCount}</span>
                )}
              </button>
              <button
                onClick={() => setActivePeopleTab('onboarding')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition ${
                  activePeopleTab === 'onboarding'
                    ? 'bg-orca-elevate text-white shadow-sm'
                    : 'text-steel hover:bg-ice'
                }`}
              >
                <UserPlus size={15} />
                Onboarding
                {newProspectCount > 0 && (
                  <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">{newProspectCount}</span>
                )}
              </button>
            </div>
          )}

          {activeTab === 'documents' && <DocumentsAdmin />}
          {activeTab === 'prompts' && (isAirlineAdmin || isSuperAdmin) && <PromptTemplatesAdmin />}
          {activeTab === 'scenarios' && <TranscriptionScenariosAdmin />}
          {activeTab === 'pilots' && <PilotsAdmin />}
          {activeTab === 'people' && isSuperAdmin && (
            <>
              {activePeopleTab === 'users' && <UsersAdmin />}
              {activePeopleTab === 'onboarding' && <OnboardingAdmin newProspectCount={newProspectCount} />}
            </>
          )}
        </div>
      )}

      {/* ── DESKTOP: tab bar + content ── */}
      <div className="hidden md:block space-y-6">
        <div className="bg-white rounded-lg shadow-md">
          <div className="flex border-b border-steel/20">
            <button onClick={() => setActiveTab('documents')} className={`flex-1 flex flex-col items-center justify-center gap-1 px-3 py-4 font-semibold text-center text-sm transition first:rounded-tl-lg last:rounded-tr-lg ${activeTab === 'documents' ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5' : 'text-steel hover:bg-ice'}`}>
              <FileText size={20} /><span>Document Library</span>
            </button>
            {(isAirlineAdmin || isSuperAdmin) && (
              <button onClick={() => setActiveTab('prompts')} className={`flex-1 flex flex-col items-center justify-center gap-1 px-3 py-4 font-semibold text-center text-sm transition first:rounded-tl-lg last:rounded-tr-lg ${activeTab === 'prompts' ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5' : 'text-steel hover:bg-ice'}`}>
                <MessageSquare size={20} /><span>Prompt Templates</span>
              </button>
            )}
            <button onClick={() => setActiveTab('scenarios')} className={`flex-1 flex flex-col items-center justify-center gap-1 px-3 py-4 font-semibold text-center text-sm transition first:rounded-tl-lg last:rounded-tr-lg ${activeTab === 'scenarios' ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5' : 'text-steel hover:bg-ice'}`}>
              <Mic size={20} /><span>Transcription Scenarios</span>
            </button>
            <button onClick={() => setActiveTab('pilots')} className={`flex-1 flex flex-col items-center justify-center gap-1 px-3 py-4 font-semibold text-center text-sm transition first:rounded-tl-lg last:rounded-tr-lg ${activeTab === 'pilots' ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5' : 'text-steel hover:bg-ice'}`}>
              <UserCircle size={20} /><span>Pilots</span>
            </button>
            {isSuperAdmin && (
              <button onClick={() => setActiveTab('people')} className={`flex-1 flex flex-col items-center justify-center gap-1 px-3 py-4 font-semibold text-center text-sm transition first:rounded-tl-lg last:rounded-tr-lg ${activeTab === 'people' ? 'text-orca-elevate border-b-2 border-orca-elevate bg-orca-elevate/5' : 'text-steel hover:bg-ice'}`}>
                <Users size={20} /><span>People</span>
                {totalPeopleCount > 0 && <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{totalPeopleCount}</span>}
              </button>
            )}
            {isSuperAdmin && (
              <button onClick={() => navigate('/admin/doc-types')} className="flex-1 flex flex-col items-center justify-center gap-1 px-3 py-4 font-semibold text-center text-sm text-steel hover:bg-ice transition first:rounded-tl-lg last:rounded-tr-lg">
                <Shield size={20} /><span>Super Admin</span>
              </button>
            )}
          </div>

          {activeTab === 'people' && isSuperAdmin && (
            <div className="flex gap-1 px-4 pt-3 pb-0 bg-orca-elevate/5 border-b border-steel/10">
              <button onClick={() => setActivePeopleTab('users')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-md transition ${activePeopleTab === 'users' ? 'bg-white text-orca-elevate border border-b-0 border-steel/20 shadow-sm' : 'text-steel hover:text-navy hover:bg-white/60'}`}>
                <Users size={15} /><span>Users</span>
                {pendingUserCount > 0 && <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">{pendingUserCount}</span>}
              </button>
              <button onClick={() => setActivePeopleTab('onboarding')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-md transition ${activePeopleTab === 'onboarding' ? 'bg-white text-orca-elevate border border-b-0 border-steel/20 shadow-sm' : 'text-steel hover:text-navy hover:bg-white/60'}`}>
                <UserPlus size={15} /><span>Onboarding</span>
                {newProspectCount > 0 && <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">{newProspectCount}</span>}
              </button>
            </div>
          )}
        </div>

        <div>
          {activeTab === 'documents' && <DocumentsAdmin />}
          {activeTab === 'prompts' && (isAirlineAdmin || isSuperAdmin) && <PromptTemplatesAdmin />}
          {activeTab === 'scenarios' && <TranscriptionScenariosAdmin />}
          {activeTab === 'pilots' && <PilotsAdmin />}
          {activeTab === 'people' && isSuperAdmin && (
            <>
              {activePeopleTab === 'users' && <UsersAdmin />}
              {activePeopleTab === 'onboarding' && <OnboardingAdmin newProspectCount={newProspectCount} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
